export type AppointmentStatus =
  | 'requested'
  | 'confirmed'
  | 'completed'
  | 'cancelled'
  | 'no_show';

export type BookingPaymentMode = 'none' | 'deposit' | 'full';
export type BookingDepositPolicy = 'keep' | 'refund';
export type AppointmentDepositResolutionStatus =
  | 'not_applicable'
  | 'kept'
  | 'refund_pending'
  | 'refunded'
  | 'refund_failed';

export type ResourceHour = {
  dayOfWeek: number;
  startTime: string;
  endTime: string;
};

/** Draft shape for the week editor (1–3 shifts per day). */
export type ResourceHourShift = { startTime: string; endTime: string };

export type ResourceHourDay = {
  dayOfWeek: number; // 0=domingo ... 6=sábado
  isOpen: boolean;
  shifts: ResourceHourShift[];
};

export type ResourceTimeOff = {
  id: string;
  resourceId: string;
  startsAt: string;
  endsAt: string;
  reason: string | null;
};

export type AppointmentResourceKind = 'person' | 'bay' | 'equipment';

export type AppointmentResource = {
  id: string;
  organizationId: string;
  branchId: string;
  displayName: string;
  employeeId: string | null;
  userId: string | null;
  color: string | null;
  isActive: boolean;
  sortOrder: number;
  /** person=operario, bay=lift/box, equipment=otro */
  resourceKind: AppointmentResourceKind;
  hours: ResourceHour[];
};

/** PELU-07: línea de servicio dentro de un turno. */
export type AppointmentItem = {
  id?: string;
  productId: string;
  sortOrder: number;
  nameSnapshot: string;
  priceSnapshot: number;
  durationMinutes: number;
};

export type Appointment = {
  id: string;
  organizationId: string;
  branchId: string;
  resourceId: string;
  /** Servicio principal (ítem sort_order 0). Compat apps viejas. */
  productId: string;
  /** PELU-07: N servicios; vacío/undefined = solo productId. */
  items?: AppointmentItem[];
  /** Clientes CRM (`customers.customers.id`); null = nombre libre / walk-in. */
  customerId: string | null;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  startsAt: string;
  endsAt: string;
  status: AppointmentStatus;
  source: 'portal' | 'app';
  notes: string | null;
  paymentMode: BookingPaymentMode;
  paymentStatus: string;
  amountTotal: number | null;
  amountPaid: number | null;
  saleId: string | null;
  arrivedAt: string | null;
  quoteRequestId: string | null;
  sessionIndex: number | null;
  /** PELU-08: sesión consumida de un paquete. */
  packageEntitlementId?: string | null;
  depositResolutionStatus?: AppointmentDepositResolutionStatus;
  depositResolutionAmount?: number | null;
  /** Display name del recurso (solo si viene de un RPC con join). */
  resourceName?: string | null;
};

export type ServiceProductOption = {
  id: string;
  name: string;
  durationMinutes: number;
  price: number;
};

export type BookingSlotIntervalMinutes = number;

export type BookingSettings = {
  bookingEnabled: boolean;
  bookingPaymentMode: BookingPaymentMode;
  /** Fixed seña in ARS; null = use percent */
  bookingDepositAmount: number | null;
  /** Seña as percent 1–100; ignored if amount set */
  bookingDepositPercent: number | null;
  /** Customer self-cancel deadline (hours before start) */
  bookingCancelHours: number;
  /** Min hours before slot for online booking */
  bookingMinLeadHours: number;
  /** How many days ahead online booking can go */
  bookingHorizonDays: number;
  /** Cancel unpaid requested after N minutes; 0 = off */
  bookingUnpaidExpireMinutes: number;
  /** Start-time step offered online (independent of service duration) */
  bookingSlotIntervalMinutes: BookingSlotIntervalMinutes;
  /** PELU-09: allow join waitlist when no slots */
  bookingWaitlistEnabled: boolean;
  /** TTL minutes for a waitlist offer */
  bookingWaitlistOfferMinutes: number;
  /** What happens to a paid deposit after a late customer cancellation. */
  bookingLateCancelPolicy: BookingDepositPolicy;
  /** What happens to a paid deposit after a no-show. */
  bookingNoShowPolicy: BookingDepositPolicy;
};

export type AppointmentWaitlistStatus =
  | 'waiting'
  | 'offered'
  | 'claimed'
  | 'expired'
  | 'cancelled';

/** PELU-09 negocio: entrada de la cola de espera vista por el staff. */
export type AppointmentWaitlistEntry = {
  id: string;
  productId: string;
  /** null = cualquier agenda de la sucursal */
  resourceId: string | null;
  customerUserId: string;
  customerEmail: string | null;
  customerName: string | null;
  preferredFrom: string | null;
  preferredTo: string | null;
  status: AppointmentWaitlistStatus;
  offeredResourceId: string | null;
  offeredStartsAt: string | null;
  offeredEndsAt: string | null;
  offerExpiresAt: string | null;
  claimedAppointmentId: string | null;
  createdAt: string;
};

export const RESOURCE_DAY_LABELS = [
  'Domingo',
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
] as const;

export const RESOURCE_DAY_SHORT = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'] as const;

/** Compact week summary: "Lun–Vie 09:00–18:00 · Sáb 09:00–13:00". */
export function summarizeResourceHours(hours: ResourceHour[]): string {
  if (hours.length === 0) return 'Sin horarios';
  const draft = hoursToWeekDraft(hours);
  const parts: string[] = [];
  let i = 0;
  while (i < 7) {
    const day = draft[i]!;
    if (!day.isOpen) {
      i += 1;
      continue;
    }
    const key = day.shifts
      .map((s) => `${s.startTime.slice(0, 5)}–${s.endTime.slice(0, 5)}`)
      .join(', ');
    let j = i;
    while (j + 1 < 7) {
      const next = draft[j + 1]!;
      if (!next.isOpen) break;
      const nextKey = next.shifts
        .map((s) => `${s.startTime.slice(0, 5)}–${s.endTime.slice(0, 5)}`)
        .join(', ');
      if (nextKey !== key) break;
      j += 1;
    }
    const dayLabel =
      i === j ? RESOURCE_DAY_SHORT[i]! : `${RESOURCE_DAY_SHORT[i]!}–${RESOURCE_DAY_SHORT[j]!}`;
    parts.push(`${dayLabel} ${key}`);
    i = j + 1;
  }
  return parts.length > 0 ? parts.join(' · ') : 'Sin horarios';
}

export function hoursToWeekDraft(hours: ResourceHour[]): ResourceHourDay[] {
  const byDay = new Map<number, ResourceHourShift[]>();
  for (const h of hours) {
    const list = byDay.get(h.dayOfWeek) ?? [];
    list.push({ startTime: h.startTime, endTime: h.endTime });
    byDay.set(h.dayOfWeek, list);
  }
  return Array.from({ length: 7 }, (_, dayOfWeek) => {
    const shifts = (byDay.get(dayOfWeek) ?? [])
      .slice()
      .sort((a, b) => a.startTime.localeCompare(b.startTime));
    return { dayOfWeek, isOpen: shifts.length > 0, shifts };
  });
}

export function weekDraftToHours(days: ResourceHourDay[]): ResourceHour[] {
  const out: ResourceHour[] = [];
  for (const d of days) {
    if (!d.isOpen) continue;
    for (const s of d.shifts) {
      const startTime = s.startTime.slice(0, 5);
      const endTime = s.endTime.slice(0, 5);
      if (!startTime || !endTime || endTime <= startTime) continue;
      out.push({ dayOfWeek: d.dayOfWeek, startTime, endTime });
    }
  }
  return out;
}

/** Copy Lun shifts onto open weekdays (Lun–Vie). */
export function applyWeekdayTemplate(days: ResourceHourDay[]): ResourceHourDay[] {
  const mon = days.find((d) => d.dayOfWeek === 1);
  const template =
    mon?.isOpen && mon.shifts.length > 0
      ? mon.shifts.map((s) => ({ ...s }))
      : days.find((d) => d.dayOfWeek >= 1 && d.dayOfWeek <= 5 && d.isOpen && d.shifts.length > 0)?.shifts.map(
          (s) => ({ ...s }),
        ) ?? [{ startTime: '09:00', endTime: '18:00' }];

  return days.map((d) => {
    if (d.dayOfWeek < 1 || d.dayOfWeek > 5 || !d.isOpen) return d;
    return { ...d, shifts: template.map((s) => ({ ...s })) };
  });
}
